// services/turnstileService.ts
type TurnstileCallback = (token: string) => void;
type TurnstileErrorCallback = (error: string) => void;
type TurnstileExpireCallback = () => void;

interface TurnstileWidgetOptions {
  sitekey: string;
  callback: TurnstileCallback;
  onError?: TurnstileErrorCallback;
  onExpire?: TurnstileExpireCallback;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact" | "flexible";
}

interface TurnstileWidget {
  id: string;
  container: HTMLElement;
  options: TurnstileWidgetOptions;
}

declare global {
  interface Window {
    turnstile: any;
    __turnstileCallbacks: Record<string, TurnstileCallback>;
  }
}

class TurnstileService {
  private static instance: TurnstileService;
  private isScriptLoaded = false;
  private isScriptLoading = false;
  private widgets: Map<string, TurnstileWidget> = new Map();
  private widgetCounter = 0;
  private pendingCallbacks: (() => void)[] = [];

  private constructor() {
    // Initialize global callbacks object
    if (!window.__turnstileCallbacks) {
      window.__turnstileCallbacks = {};
    }
  }

  static getInstance(): TurnstileService {
    if (!TurnstileService.instance) {
      TurnstileService.instance = new TurnstileService();
    }
    return TurnstileService.instance;
  }

  /**
   * Load the Turnstile script once
   */
  loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isScriptLoaded && window.turnstile) {
        resolve();
        return;
      }

      if (this.isScriptLoading) {
        this.pendingCallbacks.push(resolve);
        return;
      }

      const existingScript = document.querySelector('script[src*="turnstile"]');
      if (existingScript) {
        this.isScriptLoaded = true;
        resolve();
        return;
      }

      this.isScriptLoading = true;

      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.isScriptLoaded = true;
        this.isScriptLoading = false;

        // Resolve all pending callbacks
        this.pendingCallbacks.forEach((cb) => cb());
        this.pendingCallbacks = [];

        resolve();
      };

      script.onerror = () => {
        this.isScriptLoading = false;
        reject(new Error("Failed to load Turnstile script"));
      };

      document.body.appendChild(script);
    });
  }

  /**
   * Check if Turnstile API is available
   */
  async checkAvailability(timeout = 5000): Promise<boolean> {
    try {
      await this.loadScript();

      // Wait for turnstile to be available
      const startTime = Date.now();
      while (!window.turnstile && Date.now() - startTime < timeout) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      return !!window.turnstile;
    } catch (error) {
      console.error("Turnstile API check failed:", error);
      return false;
    }
  }

  /**
   * Render a Turnstile widget
   */
  renderWidget(
    container: HTMLElement,
    options: TurnstileWidgetOptions,
  ): string | null {
    if (!window.turnstile) {
      console.error("Turnstile not available");
      return null;
    }

    try {
      const widgetId = `turnstile-${++this.widgetCounter}`;

      const callbackName = `__turnstileCallback_${widgetId}`;

      window.__turnstileCallbacks[callbackName] = options.callback;

      const id = window.turnstile.render(container, {
        sitekey: options.sitekey,
        callback: (token: string) => {
          const cb = window.__turnstileCallbacks[callbackName];
          if (cb) {
            cb(token);
            delete window.__turnstileCallbacks[callbackName];
          }
        },
        "error-callback": options.onError,
        "expired-callback": options.onExpire,
        theme: options.theme || "auto",
        size: options.size || "normal",
        "refresh-expired": "auto",
      });

      this.widgets.set(widgetId, {
        id: id,
        container,
        options,
      });

      return widgetId;
    } catch (error) {
      console.error("Error rendering Turnstile widget:", error);
      return null;
    }
  }

  resetWidget(widgetId: string): void {
    const widget = this.widgets.get(widgetId);
    if (widget && window.turnstile) {
      try {
        window.turnstile.reset(widget.id);
      } catch (error) {
        console.error("Error resetting Turnstile widget:", error);
      }
    }
  }

  rerenderWidget(
    widgetId: string,
    container: HTMLElement,
    options: TurnstileWidgetOptions,
  ): string | null {
    this.removeWidget(widgetId);
    container.innerHTML = "";

    return this.renderWidget(container, options);
  }

  /**
   * Remove a specific widget
   */
  removeWidget(widgetId: string): void {
    const widget = this.widgets.get(widgetId);
    if (widget && window.turnstile) {
      try {
        window.turnstile.remove(widget.id);
        this.widgets.delete(widgetId);
      } catch (error) {
        console.error("Error removing Turnstile widget:", error);
      }
    }
  }

  getResponse(widgetId: string): string | null {
    const widget = this.widgets.get(widgetId);
    if (widget && window.turnstile) {
      try {
        return window.turnstile.getResponse(widget.id);
      } catch (error) {
        console.error("Error getting Turnstile response:", error);
        return null;
      }
    }
    return null;
  }
}

export const turnstileService = TurnstileService.getInstance();
